import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { LoaderCircle, RefreshCw, Sparkles } from 'lucide-react';
import { personaTestApi } from '../services/personaService';
import {
  cleanInternalCommentPrefix,
  InsightReportActionItem,
  InsightReportEvidence,
  PRIORITY_CLASS,
  PRIORITY_LABEL,
} from './InsightReportShared';

export default function InsightPanel({
  testId,
  scopeType,
  scopeIndex,
  initialInsight,
  hasInsightData,
  disabled = false,
  autoGenerate = false,
  variant = 'default',
  onTestUpdate,
  reportActionTarget = null,
}) {
  const [insight, setInsight] = useState(initialInsight || null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [lastGeneratedAt, setLastGeneratedAt] = useState(null);
  const [autoGenerateAttempted, setAutoGenerateAttempted] = useState(false);
  const isReport = variant === 'report';

  useEffect(() => {
    setInsight(initialInsight || null);
    setError('');
    setAutoGenerateAttempted(false);
  }, [initialInsight, scopeIndex, scopeType]);

  const evidenceLookup = useMemo(
    () => new Map((insight?.evidenceComments || insight?.evidence_comments || []).map((comment) => [comment.id, comment])),
    [insight],
  );

  const canRegenerate = !lastGeneratedAt || Date.now() - lastGeneratedAt > 3 * 60 * 1000;

  const handleGenerate = useCallback(async (force = false) => {
    if (disabled || isGenerating || (force && !canRegenerate)) return;

    setIsGenerating(true);
    setError('');
    try {
      const payload = await personaTestApi.generateInsights(testId, {
        scopeType,
        scopeIndex,
        force,
      });
      if (payload?.insight) {
        setInsight(payload.insight);
      }
      if (payload?.test && typeof onTestUpdate === 'function') {
        onTestUpdate(payload.test);
      }
      setLastGeneratedAt(Date.now());
    } catch (err) {
      setError(err?.message || '인사이트 생성에 실패했습니다.');
    } finally {
      setIsGenerating(false);
    }
  }, [canRegenerate, disabled, isGenerating, onTestUpdate, scopeIndex, scopeType, testId]);

  useEffect(() => {
    if (!autoGenerate || autoGenerateAttempted || disabled || !hasInsightData || insight || isGenerating) {
      return;
    }
    setAutoGenerateAttempted(true);
    void handleGenerate(false);
  }, [autoGenerate, autoGenerateAttempted, disabled, handleGenerate, hasInsightData, insight, isGenerating]);

  if (!hasInsightData) {
    return (
      <section className={`insight-panel insight-panel--empty${isReport ? ' insight-panel--report' : ''}`}>
        구조화된 코멘트 데이터가 없어 액셔너블 인사이트를 생성할 수 없습니다.
      </section>
    );
  }

  const actionItems = insight?.actionItems || insight?.action_items || [];
  const averageReactionScore = insight?.averageReactionScore ?? insight?.average_reaction_score;
  const expectedCompletionScore = insight?.expectedCompletionScore ?? insight?.expected_completion_score;
  const insightScore = averageReactionScore ?? expectedCompletionScore;
  const insightScoreLabel = averageReactionScore != null ? '평균 UX 반응 점수' : '예상 완료 점수';
  const oneLineReview = insight?.oneLineReview || insight?.one_line_review;
  const generatedAt = insight?.generatedAt || insight?.generated_at;
  const reportRegenerateAction = !disabled && insight ? (
    <button
      type="button"
      className="insight-report-regen"
      disabled={isGenerating || !canRegenerate}
      onClick={() => void handleGenerate(true)}
    >
      {isGenerating ? <LoaderCircle size={14} className="insight-panel__spin" /> : <RefreshCw size={14} />}
      다시 생성
    </button>
  ) : null;

  const dedupeEvidenceComments = (comments) => {
    const seen = new Set();
    return comments.filter((comment) => {
      const text = String(comment?.text || '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
      if (!text || seen.has(text)) return false;
      seen.add(text);
      return true;
    });
  };

  if (isReport) {
    return (
      <section className="insight-panel insight-panel--report">
        {reportActionTarget && reportRegenerateAction
          ? createPortal(reportRegenerateAction, reportActionTarget)
          : null}
        {!insight ? (
          <div className="insight-report-loading-card">
            <LoaderCircle size={22} className="insight-panel__spin" />
            <div>
              <strong>보고서를 생성하고 있습니다.</strong>
              <p>코멘트와 점수 패턴을 묶어 핵심 개선안을 정리하고 있습니다.</p>
            </div>
          </div>
        ) : (
          <>
            <section className="insight-report-hero">
              <div className="insight-report-hero__copy">
                <h2>{oneLineReview}</h2>
                {Number.isFinite(Number(insightScore)) ? (
                  <p className="insight-report-hero__score">
                    {insightScoreLabel} <strong>{Math.round(Number(insightScore))}점</strong>
                    <span>{averageReactionScore != null ? '전체 퍼소나의 명확성·사용성·만족도 점수 평균' : '기존 Flow 완료 산식 기반'}</span>
                  </p>
                ) : null}
              </div>
              <div className="insight-report-hero__actions">
                {!reportActionTarget ? reportRegenerateAction : null}
              </div>
            </section>

            <section className="insight-report-section">
              <div className="insight-report-section__head">
                <h3>주요 내용 요약</h3>
                {generatedAt ? (
                  <time dateTime={generatedAt}>
                    {new Date(generatedAt).toLocaleString('ko-KR')}
                  </time>
                ) : null}
              </div>
              <div className="insight-report-list">
                {actionItems.map((item, index) => {
                  const evidenceIds = item.evidenceCommentIds || item.evidence_comment_ids || [];
                  const evidenceComments = dedupeEvidenceComments(
                    evidenceIds
                      .map((id) => evidenceLookup.get(id))
                      .filter(Boolean),
                  );

                  return (
                    <InsightReportActionItem
                      key={`${item.priority}-${index}-${item.title}`}
                      item={item}
                    >
                      <InsightReportEvidence
                        items={evidenceComments}
                        sourceLabel="화면 테스트 코멘트"
                        cleanText
                      />
                    </InsightReportActionItem>
                  );
                })}
              </div>
            </section>
          </>
        )}

        {error ? <p className="insight-panel__error">{error}</p> : null}
      </section>
    );
  }

  return (
    <section className="insight-panel">
      <div className="insight-panel__header">
        <div>
          <div className="insight-panel__badge">
            <Sparkles size={12} />
            코멘트 기반 액셔너블 인사이트
          </div>
          <h3>최종적으로 무엇이 문제이고, 무엇부터 고쳐야 하는지 요약합니다</h3>
          <p>중요요소 매핑, 긍부정 방향, 심각도를 기반으로 중요도 상/중/하 개선안을 생성합니다.</p>
        </div>
        {insight && !disabled ? (
          <button
            type="button"
            className="insight-panel__ghost-btn"
            disabled={isGenerating || !canRegenerate}
            onClick={() => void handleGenerate(true)}
          >
            {isGenerating ? <LoaderCircle size={14} className="insight-panel__spin" /> : <RefreshCw size={14} />}
            다시 생성
          </button>
        ) : null}
      </div>

      {!insight ? (
        <div className="insight-panel__empty-cta">
          <p>
            아직 생성된 인사이트가 없습니다. 버튼을 누르면 이 스코프의 코멘트만 모아
            한 번 생성하고 결과를 캐시합니다.
          </p>
          {!disabled ? (
            <div className="insight-panel__cta-row">
              <button
                type="button"
                className="insight-panel__primary-btn"
                disabled={isGenerating}
                onClick={() => void handleGenerate(false)}
              >
                {isGenerating ? <LoaderCircle size={14} className="insight-panel__spin" /> : <Sparkles size={14} />}
                인사이트 생성
              </button>
              <span>GPT 5.4 · 평균 10~30초 · 첫 생성 후 캐시</span>
            </div>
          ) : (
            <p className="insight-panel__share-note">공유 보기에서는 인사이트를 생성할 수 없습니다.</p>
          )}
        </div>
      ) : (
        <div className="insight-panel__body">
          <div className="insight-panel__summary">
            {generatedAt ? (
              <div className="insight-panel__summary-meta">
                <em>{new Date(generatedAt).toLocaleString('ko-KR')}</em>
              </div>
            ) : null}
            <p>{oneLineReview}</p>
          </div>

          {actionItems.map((item, index) => {
            const evidenceIds = item.evidenceCommentIds || item.evidence_comment_ids || [];
            const evidenceComments = evidenceIds
              .map((id) => evidenceLookup.get(id))
              .filter(Boolean);

            return (
              <article
                key={`${item.priority}-${index}-${item.title}`}
                className={`insight-panel__item ${PRIORITY_CLASS[item.priority] || ''}`}
              >
                <div className="insight-panel__item-head">
                  <span>{PRIORITY_LABEL[item.priority] || item.priority}</span>
                  <h4>{item.title}</h4>
                </div>
                <p><strong>문제: </strong>{item.issue}</p>
                <p><strong>개선: </strong>{item.solution}</p>
                {evidenceComments.length > 0 ? (
                  <details>
                    <summary>{`관련 코멘트 ${evidenceComments.length}개 보기`}</summary>
                    <div className="insight-panel__evidence">
                      {evidenceComments.map((comment) => (
                        <p key={comment.id}>
                          <strong>{comment.personaName || comment.persona_name}</strong>
                          {`: ${cleanInternalCommentPrefix(comment.text)}`}
                        </p>
                      ))}
                    </div>
                  </details>
                ) : null}
              </article>
            );
          })}
        </div>
      )}

      {isGenerating && insight ? (
        <p className="insight-panel__status">새 인사이트를 생성 중입니다.</p>
      ) : null}
      {error ? <p className="insight-panel__error">{error}</p> : null}
    </section>
  );
}
